import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  GUEST_SHARE_DENIED,
  guestShareWasBlocked,
} from "../../lib/guest-share";
import { useAuth } from "../../lib/useAuth";
import { Input } from "../../components/ui/Input";
import {
  cleanCodeInput,
  isCompleteCode,
  MAX_CODE_LENGTH,
  SHORT_CODE_LENGTH,
} from "../../lib/verification-code";
import {
  claimVerificationLink,
  verificationLinkCode,
} from "../../lib/verify-email-link";

const LINK_VERIFY_ERROR = "This link did not verify your email.";

// MOSA-694 item 6: how long the confirmation holds the screen before the
// dashboard opens.
export const CONFIRMATION_MS = 1500;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const linkCode = verificationLinkCode(location.search);
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // MOSA-694 item 5a: the link in the email is the main path, so the code
  // field starts closed.
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [verified, setVerified] = useState(false);
  const [linkPhase, setLinkPhase] = useState<"idle" | "verifying" | "failed">(
    linkCode ? "verifying" : "idle",
  );
  // verifyEmail signs the user in before it resolves, so this screen renders
  // once with isAuthenticated already true and `verified` still false. A ref
  // is set before the call so the guard below can tell "we are mid-verify"
  // from "somebody opened this screen while signed in".
  const ownVerifyRef = useRef(false);
  const claimedLinks = useRef(new Set<string>());
  const codeRef = useRef<HTMLInputElement>(null);
  const params = new URLSearchParams(location.search);
  const email = params.get("email");
  const invite = params.get("invite")?.trim() ?? "";
  // MOSA-364: the connect wizard's return path, carried through sign-up.
  const nextParam = params.get("next")?.trim() ?? "";
  const nextPath =
    nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "";
  // MOSA-438: keep pilot for later reads after sign-up.
  const pilotParam = params.get("pilot");
  const emailDecoded = email ? decodeURIComponent(email) : null;

  const { resendVerificationEmail, verifyEmail, isAuthenticated } = useAuth();

  const destination =
    invite.length > 0 ? `/invite/${invite}` : nextPath ? nextPath : "/data-sources";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // MOSA-696 item 5: the code is 8 characters now. A 32-character code from
    // an email sent before the rollout still verifies, so both shapes pass.
    if (!isCompleteCode(code)) {
      toast.error(`Enter the ${SHORT_CODE_LENGTH}-character code from the email.`);
      return;
    }
    setIsLoading(true);
    ownVerifyRef.current = true;

    try {
      await verifyEmail(code);
      if (guestShareWasBlocked()) {
        toast.error(GUEST_SHARE_DENIED);
        navigate("/data-sources");
        return;
      }
      // The confirmation below replaces the success toast. Two copies of the
      // same message is the repeated text the board asked us to remove.
      setVerified(true);
    } catch (error) {
      ownVerifyRef.current = false;
      console.error("Verification error:", error);
      toast.error("Email verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!verified) {
      return;
    }
    const timer = setTimeout(() => navigate(destination), CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [verified, destination, navigate]);

  useEffect(() => {
    const codeFromLink = claimVerificationLink(
      location.search,
      claimedLinks.current,
    );
    if (!codeFromLink) return;
    ownVerifyRef.current = true;
    setLinkPhase("verifying");
    setShowCodeForm(false);
    verifyEmail(codeFromLink)
      .then(() => {
        if (guestShareWasBlocked()) {
          toast.error(GUEST_SHARE_DENIED);
          navigate("/data-sources");
          return;
        }
        setVerified(true);
      })
      .catch(() => {
        ownVerifyRef.current = false;
        setLinkPhase("failed");
        setShowCodeForm(true);
      });
  }, [location.search, navigate, verifyEmail]);

  useEffect(() => {
    if (showCodeForm) {
      codeRef.current?.focus();
    }
  }, [showCodeForm]);

  const handleResendCode = async () => {
    if (!emailDecoded) {
      toast.error("No email to resend to");
      return;
    }

    try {
      await resendVerificationEmail(emailDecoded);
      toast.success(`We sent the email again to ${emailDecoded}.`);
    } catch (error: any) {
      console.error("Resend error:", error);
      toast.error(
        error.response?.data?.message || "Failed to resend verification code"
      );
    }
  };

  const authSearch = (() => {
    const next = new URLSearchParams();
    if (invite) next.set("invite", invite);
    if (nextPath) next.set("next", nextPath);
    if (pilotParam != null && pilotParam !== "") next.set("pilot", pilotParam);
    const qs = next.toString();
    return qs ? `?${qs}` : "";
  })();

  // Set before the guard, on the render that shows "Verifying…", so a
  // successful verifyEmail (which signs the user in) does not redirect
  // before the confirmation.
  if (linkPhase === "verifying") {
    ownVerifyRef.current = true;
  }

  // A visitor who is already signed in has no verify step left. App.tsx used
  // to redirect before this screen mounted; the guard lives here now so that
  // the confirmation can render after verifyEmail signs the user in.
  if (isAuthenticated && !ownVerifyRef.current) {
    return <Navigate to="/data-sources" replace />;
  }

  return (
    <div className="min-h-screen bg-od-bg flex flex-col items-center justify-center px-4 py-8">
      <div className="mb-8 text-center">
        {/* MOSA-694 item 4: the same wordmark the sign-in screen shows. */}
        <a
          href="https://onlydash.io"
          className="inline-flex min-h-11 min-w-11 items-center sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 text-3xl font-bold tracking-tight text-od-text rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-bg"
        >
          onlydash<span className="text-od-primary">.</span>
        </a>
        <h1 className="mt-3 text-2xl font-semibold text-od-text">
          {verified ? "Email verified" : "Verify your email"}
        </h1>
        {!verified && (
          <p className="mt-2 text-od-muted text-base">
            {emailDecoded
              ? `We sent a verification link to ${emailDecoded}.`
              : "We sent a verification link to your email address."}
          </p>
        )}
      </div>

      <div className="max-w-lg w-full space-y-6 bg-od-surface p-8 rounded-2xl shadow-lg border border-od-border">
        {verified ? (
          <p role="status" className="text-center text-od-text text-base">
            We verified your email address. We open your dashboard now.
          </p>
        ) : linkPhase === "verifying" ? (
          <p role="status" className="text-center text-od-text text-base">
            Verifying…
          </p>
        ) : (
          <>
            <div className="space-y-3">
              {linkPhase === "failed" ? (
                <p
                  role="alert"
                  className="bg-od-surface border border-od-danger text-od-danger px-3 py-2 rounded-lg text-sm"
                >
                  {LINK_VERIFY_ERROR}
                </p>
              ) : (
                <p className="text-od-text text-base">
                  Open the link in that email to verify your address.
                </p>
              )}
              <Button
                onClick={handleResendCode}
                variant="secondary" className="w-full text-sm font-semibold hover:border-od-primary"
              >
                Send the email again
              </Button>
            </div>

            <div className="border-t border-od-border pt-5 space-y-3">
              <div className="text-center">
                <Button
                  onClick={() => setShowCodeForm((open) => !open)}
                  aria-expanded={showCodeForm}
                  aria-controls={showCodeForm ? "verify-code-form" : undefined}
                  variant="link" size="sm" className="text-xs text-od-muted hover:text-od-text hover:underline"
                >
                  Enter the code instead
                </Button>
              </div>

              {showCodeForm && (
                <form
                  id="verify-code-form"
                  onSubmit={handleSubmit}
                  className="space-y-3"
                >
                  <div>
                    <label
                      htmlFor="code"
                      className="text-xs font-semibold text-od-text block mb-1.5"
                    >
                      Verification code
                    </label>
                    {/* MOSA-696 item 5: 8 characters need a field that holds
                        8 characters, not the width of the card. */}
                    <Input
                      id="code"
                      ref={codeRef}
                      type="text"
                      required
                      disabled={isLoading}
                      value={code}
                      onChange={(e) => setCode(cleanCodeInput(e.target.value))}
                      className="font-mono w-40 tracking-[0.2em] placeholder:tracking-normal"
                      placeholder="8 characters"
                      maxLength={MAX_CODE_LENGTH}
                      inputMode="text"
                      autoCapitalize="characters"
                      autoComplete="one-time-code"
                      spellCheck={false}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    variant="secondary" className="w-full text-sm font-semibold hover:border-od-primary"
                  >
                    {isLoading ? "Verifying…" : "Verify email"}
                  </Button>
                </form>
              )}
            </div>

            <div className="text-center">
              <Link
                to={
                  authSearch
                    ? { pathname: "/auth", search: authSearch }
                    : "/auth"
                }
                className="inline-flex min-h-11 min-w-11 items-center rounded-sm text-od-primary-text hover:underline font-semibold text-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
              >
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
